import type { TextLayer } from '../template';
import {
  emph,
  lin,
  linStops,
  solid,
  step,
  t,
  type Box,
  type MotionOpts,
  type RelKey,
  type SceneRef,
  type Tx,
  type VideoBuilder,
} from '../video-gallery/kit';
import { GEN_TITLES, type VideoContent } from './content';
import { plain, type GenPalette } from './palette';

/**
 * Scene blocks of generated videos. A block is one kind of scene (hook, showcase, benefits…)
 * with several layouts ("variants"); each lays itself out for any format from the content it is
 * given and animates its parts by role. The planner (planner.ts) strings them together.
 */

export type Theme = 'dark' | 'light';

export interface BlockCtx {
  b: VideoBuilder;
  sc: SceneRef;
  c: VideoContent;
  p: GenPalette;
  theme: Theme;
  /** Random numbers of this scene (seeded: the same storyboard gives the same video). */
  rnd: () => number;
}

export interface BlockVariant {
  id: string;
  /** Can the content fill it? */
  fits: (c: VideoContent) => boolean;
  /** Scene length, ms (before the pace is applied). */
  duration: (c: VideoContent) => number;
  /**
   * Animates layers with clip paths (wipes, masks): such scenes must not be entered through a
   * clip transition, which would override those clips.
   */
  clip?: boolean;
  /** Themes it is designed for (both by default). */
  themes?: Theme[];
  build: (x: BlockCtx) => void;
}

export interface BlockDef {
  id: BlockId;
  name: Tx;
  /** Where it can go: the first scene, the last one or in between. */
  place: 'first' | 'middle' | 'last';
  variants: BlockVariant[];
}

export const BLOCK_IDS = [
  'hook',
  'showcase',
  'benefits',
  'stats',
  'gallery',
  'offer',
  'quote',
  'cta',
] as const;
export type BlockId = (typeof BLOCK_IDS)[number];

// ---------------------------------------------------------------------------------------------
// Shared pieces

const photos = (c: VideoContent) => (c.photos ?? []).filter(Boolean);
const benefits = (c: VideoContent) => (c.benefits ?? []).filter((v) => v.trim());
const stats = (c: VideoContent) => (c.stats ?? []).filter((s) => s.value.trim());

function look(x: BlockCtx) {
  const { p } = x;
  return x.theme === 'dark'
    ? { fg: '#FFFFFF', muted: p.mutedDark, hi: p.accent, onHi: p.onAccent, card: '#FFFFFF' }
    : { fg: p.ink, muted: p.mutedLight, hi: p.primary, onHi: p.onPrimary, card: '#FFFFFF' };
}

function backdrop(x: BlockCtx) {
  const { b, sc, p } = x;
  b.backdrop(
    sc,
    `${sc.key}_bg`,
    x.theme === 'dark' ? lin(160, p.dark, p.dark2) : lin(180, p.light, p.paper),
  );
  dress(x);
}

/** `#RRGGBB` with an alpha (0..1). */
const alpha = (hex: string, a: number) =>
  `${plain(hex).slice(0, 7)}${Math.round(a * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()}`;

/**
 * Background decoration of a scene, between its backdrop and its content: soft glows, rings,
 * diagonal bands, a cluster of dots or the user's photo blurred behind everything. One or two
 * per scene, in the palette, moving slowly — so no frame is a bare gradient.
 */
function dress(x: BlockCtx) {
  const { b, sc, c, p } = x;
  const W = b.W;
  const H = b.H;
  const u = b.u;
  const dark = x.theme === 'dark';
  const tints = [look(x).hi, ...p.extras].map(plain);
  const kinds: string[] = ['glow', 'glow', 'rings', 'bands', 'dots'];
  // The user's photo behind dark scenes that are not about photos already
  const ph = (c.photos ?? [])[Math.floor(x.rnd() * (c.photos?.length ?? 0))];
  if (ph && dark && !/^(showcase|gallery|hook)/.test(sc.key)) kinds.push('photo', 'photo');
  const first = pick(x, kinds);
  const used = [first];
  if (first !== 'photo' && x.rnd() < 0.45)
    used.push(
      pick(
        x,
        kinds.filter((k) => k !== first && k !== 'photo'),
      ),
    );
  const corner = () => (x.rnd() < 0.5 ? 0.2 : 0.8);

  for (const kind of used) {
    if (kind === 'photo') {
      b.image(
        sc,
        'bg_photo',
        ph!,
        b.full(),
        { blur: Math.round(u * 0.012), brightness: 0.75 },
        { keys: b.kenBurns(1.06, 1.16) },
      );
      b.shape(sc, 'bg_photo_tint', b.full(), {
        fill: linStops(170, [
          [0, alpha(p.dark, 0.72)],
          [1, alpha(p.dark2, 0.9)],
        ]),
      });
    } else if (kind === 'glow') {
      [0, 1, 2].forEach((i) => {
        const d = u * (0.7 + x.rnd() * 0.5);
        const cx = W * (i === 2 ? 0.5 : i ? 1 - corner() : corner());
        const cy = H * (i === 2 ? 0.55 : i ? 0.85 : 0.15);
        const box = clampBox({ x: cx - d / 2, y: cy - d / 2, w: d, h: d }, W, H);
        const tint = tints[(i + Math.floor(x.rnd() * 3)) % tints.length];
        b.shape(
          sc,
          'bg_glow',
          box,
          {
            shape: 'ellipse',
            fill: {
              type: 'radial',
              stops: [
                { offset: 0, color: alpha(tint, dark ? 0.42 : 0.3) },
                { offset: 1, color: alpha(tint, 0) },
              ],
            },
          },
          {
            keys: [
              { at: 0, x: 0, y: 0, scale: 0.9, easing: 'ease-in-out' },
              {
                at: -1,
                x: Math.round((x.rnd() - 0.5) * u * 0.12),
                y: Math.round((x.rnd() - 0.5) * u * 0.12),
                scale: 1.1,
              },
            ],
          },
        );
      });
    } else if (kind === 'rings') {
      const cx = W * corner();
      const cy = H * (x.rnd() < 0.5 ? 0.18 : 0.82);
      [0.5, 0.75, 1].forEach((k, i) => {
        const d = u * 0.62 * k;
        b.shape(
          sc,
          'bg_ring',
          clampBox({ x: cx - d / 2, y: cy - d / 2, w: d, h: d }, W, H),
          {
            shape: 'ellipse',
            fill: null,
            strokeWidth: Math.max(2, Math.round(u * 0.006)),
            strokeColor: alpha(tints[0], dark ? 0.35 : 0.3),
          },
          {
            enter: step('zoom-in', i * 150, 700, 'out-expo'),
            emphasis: emph('breathe', 700 + i * 150, 2400, 0),
          },
        );
      });
    } else if (kind === 'bands') {
      const n = 3;
      for (let i = 0; i < n; i++) {
        const h = u * (0.05 + x.rnd() * 0.08);
        const y = H * (0.15 + i * 0.3 + x.rnd() * 0.08);
        const box = clampBox({ x: W * 0.02, y, w: W * 0.96, h }, W, H);
        b.shape(
          sc,
          'bg_band',
          box,
          {
            fill: solid(alpha(tints[i % tints.length], dark ? 0.16 : 0.12)),
            borderRadius: h / 2,
            rotation: -12,
          },
          {
            keys: [
              { at: 0, x: Math.round(u * (i % 2 ? 0.08 : -0.08)), easing: 'linear' },
              { at: -1, x: Math.round(u * (i % 2 ? -0.08 : 0.08)) },
            ],
          },
        );
      }
    } else if (kind === 'dots') {
      const cols = 4;
      const rows = 3;
      const g = u * 0.05;
      const d = u * 0.014;
      const ox = x.rnd() < 0.5 ? W * 0.04 : W * 0.96 - g * (cols - 1) - d;
      const oy = x.rnd() < 0.5 ? H * 0.04 : H * 0.96 - g * (rows - 1) - d;
      for (let r = 0; r < rows; r++)
        for (let k = 0; k < cols; k++)
          b.shape(
            sc,
            'bg_dot',
            { x: ox + k * g, y: oy + r * g, w: d, h: d },
            {
              shape: 'ellipse',
              fill: solid(alpha(tints[0], dark ? 0.6 : 0.45)),
            },
            { enter: step('pop', 100 + (r * cols + k) * 40, 300) },
          );
    }
  }
}

/** Keeps a decoration box inside the canvas (shrinks it if it would stick out). */
function clampBox(box: Box, W: number, H: number): Box {
  const w = Math.min(box.w, W);
  const h = Math.min(box.h, H);
  return { x: Math.max(0, Math.min(W - w, box.x)), y: Math.max(0, Math.min(H - h, box.y)), w, h };
}

const head = (x: BlockCtx, size: number, extra: Partial<TextLayer> = {}): Partial<TextLayer> => ({
  fontFamily: x.p.heading,
  fontWeight: 800,
  fontSize: x.b.s(size),
  color: look(x).fg,
  align: 'left',
  lineHeight: 1.04,
  ...extra,
});
const body = (x: BlockCtx, size: number, extra: Partial<TextLayer> = {}): Partial<TextLayer> => ({
  fontFamily: x.p.body,
  fontWeight: 500,
  fontSize: x.b.s(size),
  color: look(x).muted,
  align: 'left',
  lineHeight: 1.3,
  ...extra,
});

const pick = <T>(x: BlockCtx, items: readonly T[]): T => items[Math.floor(x.rnd() * items.length)];

/** Headline split into the main part and the last words, set off in the accent colour. */
function splitAccent(text: string): [string, string] {
  const words = text.trim().split(/\s+/);
  if (words.length < 3) return [text.trim(), ''];
  const n = Math.max(1, Math.round(words.length / 3));
  return [words.slice(0, -n).join(' '), words.slice(-n).join(' ')];
}

/** Flies in from an offset with a turn, then floats until the scene ends. */
function flyKeys(b: VideoBuilder, delay: number, from: { x?: number; y?: number }, turn: number) {
  const keys: RelKey[] = [
    { at: 0, x: from.x ?? 0, y: from.y ?? 0, rotate: turn, opacity: 0 },
    { at: delay, x: from.x ?? 0, y: from.y ?? 0, rotate: turn, opacity: 0, easing: 'out-expo' },
    { at: delay + 900, x: 0, y: 0, rotate: 0, opacity: 1, easing: 'ease-in-out' },
    { at: -1, x: 0, y: -b.s(0.012), rotate: 0, opacity: 1 },
  ];
  return keys;
}

/** Slides in sideways by a fixed distance (the slide preset's distance depends on the size). */
function slideKeys(b: VideoBuilder, delay: number, dir: 'left' | 'right'): RelKey[] {
  const dx = dir === 'left' ? -b.s(0.3) : b.s(0.3);
  return [
    { at: 0, x: dx, opacity: 0 },
    { at: delay, x: dx, opacity: 0, easing: 'out-quart' },
    { at: delay + 600, x: 0, opacity: 1 },
  ];
}

/** A title over the scene: rising word by word. */
function title(
  x: BlockCtx,
  name: string,
  text: string,
  box: Box,
  size = 0.085,
  align: 'left' | 'center' = 'left',
) {
  x.b.text(x.sc, name, text, box, head(x, size, { align, verticalAlign: 'top' }), {
    enter: step('words-rise', 150, 550),
    split: { by: 'word', stagger: 80 },
  });
}

function button(
  x: BlockCtx,
  label: string,
  at: { x: number; y: number; h: number; align?: 'left' | 'center' },
  delay: number,
) {
  const L = look(x);
  x.b.chip(
    x.sc,
    'cta_button',
    label,
    at,
    {
      bg: L.hi,
      color: L.onHi,
      font: x.p.heading,
      weight: 800,
      size: Math.round(at.h * 0.4),
      extra: { shadow: { x: 0, y: x.b.s(0.015), blur: x.b.s(0.05), color: '#00000040' } },
    },
    {
      enter: step('pop', delay, 550, 'spring-bouncy'),
      emphasis: emph('pulse', delay + 700, 900, 0),
    },
  );
}

/** A white row with a round marker and a label. */
function row(x: BlockCtx, name: string, label: string, box: Box, m: MotionOpts, icon = 'check') {
  const { b, sc, p } = x;
  b.shape(
    sc,
    `${name}_card`,
    box,
    {
      fill: solid('#FFFFFF'),
      borderRadius: Math.round(box.h * 0.28),
      shadow: { x: 0, y: b.s(0.012), blur: b.s(0.04), color: '#0000002E' },
    },
    m,
  );
  const d = box.h * 0.56;
  const dot = { x: box.x + box.h * 0.24, y: box.y + (box.h - d) / 2, w: d, h: d };
  b.shape(sc, `${name}_dot`, dot, { shape: 'ellipse', fill: solid(p.primary) }, m);
  b.icon(
    sc,
    `${name}_icon`,
    icon,
    { x: dot.x + d * 0.22, y: dot.y + d * 0.22, w: d * 0.56, h: d * 0.56 },
    plain(p.onPrimary),
    m,
  );
  b.text(
    sc,
    name,
    label,
    { x: dot.x + d + box.h * 0.24, y: box.y, w: box.w - d - box.h * 0.72, h: box.h },
    {
      fontFamily: p.heading,
      fontWeight: 700,
      fontSize: Math.round(box.h * 0.34),
      color: p.ink,
      align: 'left',
      maxLines: 1,
    },
    m,
  );
}

/** A photo card with rounded corners and a shadow. */
function photoCard(
  x: BlockCtx,
  name: string,
  src: string,
  box: Box,
  rotation: number,
  m: MotionOpts,
) {
  return x.b.image(
    x.sc,
    name,
    src,
    box,
    {
      borderRadius: Math.round(Math.min(box.w, box.h) * 0.07),
      rotation,
      shadow: { x: 0, y: x.b.s(0.02), blur: x.b.s(0.06), color: '#00000070' },
    },
    m,
  );
}

/** Kicker chip: the brand name over the headline. */
function kicker(x: BlockCtx, at: { x: number; y: number; align?: 'left' | 'center' }) {
  if (!x.c.brand) return;
  const h = x.b.s(0.06);
  x.b.chip(
    x.sc,
    'kicker',
    x.c.brand,
    { ...at, h },
    {
      bg: x.theme === 'dark' ? '#FFFFFF26' : '#0000000F',
      color: look(x).muted,
      upper: true,
      weight: 800,
      size: Math.round(h * 0.38),
    },
    { enter: step('fade', 0, 400) },
  );
}

// ---------------------------------------------------------------------------------------------
// Hook

const hookAccent: BlockVariant = {
  id: 'accent',
  clip: true,
  fits: () => true,
  duration: () => 3000,
  build(x) {
    const { b, sc, c } = x;
    const F = b.F;
    const up = b.upright;
    backdrop(x);
    const copy = b.pick({
      upright: b.in(0, 0, 1, 0.5),
      square: b.in(0, 0.06, 0.56, 0.88),
      wide: b.in(0, 0.08, 0.54, 0.84),
    });
    kicker(x, { x: copy.x, y: copy.y });
    const [main, accent] = splitAccent(c.headline);
    b.text(
      sc,
      'headline',
      main,
      { x: copy.x, y: copy.y + copy.h * 0.15, w: copy.w, h: copy.h * (accent ? 0.34 : 0.54) },
      head(x, up ? 0.12 : 0.1, { verticalAlign: 'bottom' }),
      { enter: step('words-rise', 250, 600), split: { by: 'word', stagger: 110 } },
    );
    if (accent)
      b.text(
        sc,
        'accent',
        accent,
        { x: copy.x, y: copy.y + copy.h * 0.5, w: copy.w, h: copy.h * 0.2 },
        head(x, up ? 0.12 : 0.1, { color: look(x).hi, maxLines: 1 }),
        {
          enter: step('pop', 800, 550, 'spring-bouncy'),
          emphasis: emph('wiggle', 1400, 500, 2, 250),
        },
      );
    if (c.subline)
      b.text(
        sc,
        'subline',
        c.subline,
        { x: copy.x, y: copy.y + copy.h * 0.73, w: up ? copy.w * 0.55 : copy.w, h: copy.h * 0.24 },
        body(x, 0.036, { verticalAlign: 'top', maxLines: 4 }),
        { enter: step('rise', 1200, 500) },
      );

    // Card: a photo, or an abstract screen with a play button
    const cardH = b.pick({ upright: F.h * 0.42, square: F.h * 0.8, wide: F.h * 0.86 });
    const cardW = cardH * 0.5625;
    const card: Box = b.pick({
      upright: { x: F.x + F.w - cardW - F.w * 0.04, y: F.y + F.h * 0.55, w: cardW, h: cardH },
      other: { x: F.x + F.w - cardW - F.w * 0.06, y: F.y + (F.h - cardH) / 2, w: cardW, h: cardH },
    });
    b.shape(
      sc,
      'card_ghost',
      { ...card, x: card.x - cardW * 0.14, y: card.y + cardW * 0.08 },
      { fill: solid(plain(look(x).hi)), borderRadius: cardW * 0.1, opacity: 0.3, rotation: -4 },
      { enter: step('fade', 1000, 500) },
    );
    const keys = flyKeys(b, 500, { x: Math.round(b.W * 0.5) }, 14);
    const cm: MotionOpts = { keys, emphasis: emph('float', 1500, 3000, 0) };
    const ph = photos(c)[0];
    if (ph) photoCard(x, 'card_photo', ph, card, 5, cm);
    else {
      b.shape(
        sc,
        'card',
        card,
        {
          fill: lin(160, look(x).hi, x.p.extras[2] ?? look(x).hi),
          borderRadius: cardW * 0.1,
          rotation: 5,
          shadow: { x: 0, y: b.s(0.03), blur: b.s(0.08), color: '#00000080' },
        },
        cm,
      );
      const pd = cardW * 0.36;
      const play = b.centered(card.x + cardW / 2, card.y + cardH * 0.55, pd, pd);
      const pm: MotionOpts = { keys, emphasis: emph('pulse', 1700, 900, 0) };
      b.shape(sc, 'card_play', play, { shape: 'ellipse', fill: solid('#FFFFFF') }, pm);
      b.icon(
        sc,
        'card_play_icon',
        'play',
        b.centered(play.x + pd * 0.54, play.y + pd / 2, pd * 0.46, pd * 0.46),
        plain(x.p.primary),
        pm,
      );
    }
    const bar: Box = up
      ? { x: F.x, y: card.y + cardH - b.s(0.02), w: card.x - F.x - b.s(0.08), h: b.s(0.014) }
      : { x: copy.x, y: copy.y + copy.h * 0.97, w: copy.w * 0.8, h: b.s(0.012) };
    b.shape(sc, 'progress_track', bar, {
      fill: solid(x.theme === 'dark' ? '#FFFFFF2E' : '#0000001A'),
      borderRadius: bar.h,
    });
    b.shape(
      sc,
      'progress_fill',
      bar,
      { fill: solid(look(x).hi), borderRadius: bar.h },
      { enter: step('wipe', 1100, 1700, 'linear') },
    );
  },
};

const hookCenter: BlockVariant = {
  id: 'center',
  clip: true,
  fits: () => true,
  duration: () => 2800,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    let top = F.y + F.h * 0.18;
    if (c.logo) {
      const s = b.s(0.16);
      b.image(
        sc,
        'logo',
        c.logo,
        b.centered(b.cx, F.y + F.h * 0.12, s, s),
        { fit: 'contain' },
        { enter: step('pop', 100, 600, 'spring-bouncy'), emphasis: emph('float', 900, 3000, 0) },
      );
      top = F.y + F.h * 0.26;
    } else kicker(x, { x: b.cx, y: F.y + F.h * 0.1, align: 'center' });
    const hb: Box = { x: F.x, y: top, w: F.w, h: F.h * 0.38 };
    b.text(sc, 'headline', c.headline, hb, head(x, b.upright ? 0.13 : 0.11, { align: 'center' }), {
      enter: step('zoom-in', 300, 700, 'out-expo'),
      emphasis: emph('breathe', 1200, 2400, 0),
    });
    const ul: Box = {
      x: b.cx - F.w * 0.2,
      y: hb.y + hb.h + b.s(0.02),
      w: F.w * 0.4,
      h: b.s(0.016),
    };
    b.shape(
      sc,
      'underline',
      ul,
      { fill: solid(look(x).hi), borderRadius: ul.h },
      { enter: step('wipe', 900, 600, 'out-quart') },
    );
    if (c.subline)
      b.text(
        sc,
        'subline',
        c.subline,
        { x: F.x + F.w * 0.08, y: ul.y + b.s(0.05), w: F.w * 0.84, h: F.h * 0.18 },
        body(x, 0.04, { align: 'center', verticalAlign: 'top', maxLines: 3 }),
        { enter: step('fade', 1200, 600) },
      );
  },
};

const hookPhoto: BlockVariant = {
  id: 'photo',
  clip: true,
  themes: ['dark'],
  fits: (c) => photos(c).length > 0,
  duration: () => 3200,
  build(x) {
    const { b, sc, c } = x;
    b.image(
      sc,
      'hero_photo',
      photos(c)[0],
      b.full(),
      {},
      { keys: b.kenBurns(1.04, 1.18, -0.02, -0.02) },
    );
    b.shade(sc, 'hero_shade', b.full(), [
      [0, '#00000000'],
      [0.45, '#00000033'],
      [1, '#000000D9'],
    ]);
    const F = b.F;
    const box = b.in(0, b.upright ? 0.5 : 0.42, b.wide ? 0.62 : 1, b.upright ? 0.34 : 0.4);
    kicker(x, { x: box.x, y: box.y - b.s(0.09) });
    b.text(
      sc,
      'headline',
      c.headline,
      box,
      head(x, b.upright ? 0.11 : 0.09, { color: '#FFFFFF', verticalAlign: 'bottom' }),
      { enter: step('lines-mask', 300, 700), split: { by: 'line', stagger: 140 } },
    );
    if (c.subline)
      b.text(
        sc,
        'subline',
        c.subline,
        {
          x: box.x,
          y: box.y + box.h + b.s(0.02),
          w: box.w,
          h: F.y + F.h - box.y - box.h - b.s(0.02),
        },
        body(x, 0.036, { color: '#FFFFFFD9', verticalAlign: 'top', maxLines: 3 }),
        { enter: step('fade', 1200, 600) },
      );
  },
};

/** The headline beat by beat, a couple of words at a time, then whole. */
function chunks(text: string): string[] {
  const words = text.trim().split(/\s+/);
  const size = Math.max(1, Math.ceil(words.length / 4));
  const out: string[] = [];
  for (let i = 0; i < words.length; i += size) out.push(words.slice(i, i + size).join(' '));
  return out;
}
const BEAT = 520;
const hookKinetic: BlockVariant = {
  id: 'kinetic',
  fits: (c) => c.headline.trim().split(/\s+/).length >= 3,
  duration: (c) => chunks(c.headline).length * BEAT + 1900,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const parts = chunks(c.headline);
    const D = sc.duration;
    const box = b.in(0, 0.28, 1, 0.34);
    parts.forEach((part, i) => {
      const start = i * BEAT;
      b.text(
        sc,
        `beat_${i + 1}`,
        part,
        box,
        head(x, b.upright ? 0.17 : 0.14, {
          align: 'center',
          fontWeight: 900,
          color: i % 2 ? look(x).hi : look(x).fg,
          maxLines: 2,
        }),
        {
          enter: step(
            pick(x, ['pop', 'zoom-in', 'drop', 'stretch'] as const),
            start,
            260,
            'out-expo',
          ),
          exit: step('fade', Math.max(0, D - start - BEAT), 120),
        },
      );
    });
    const after = parts.length * BEAT;
    b.text(
      sc,
      'headline',
      c.headline,
      b.in(0, 0.22, 1, 0.42),
      head(x, b.upright ? 0.11 : 0.09, { align: 'center' }),
      { enter: step('words-rise', after, 500), split: { by: 'word', stagger: 60 } },
    );
    if (c.subline)
      b.text(
        sc,
        'subline',
        c.subline,
        b.in(0.06, 0.68, 0.88, 0.16),
        body(x, 0.038, { align: 'center', verticalAlign: 'top', maxLines: 3 }),
        { enter: step('fade', after + 500, 500) },
      );
  },
};

// ---------------------------------------------------------------------------------------------
// Showcase: the product in pictures

/** A short line for scenes that need a title: the brand, else the subline, else the headline. */
const label = (c: VideoContent) => c.brand || c.subline || c.headline;

const showcaseFan: BlockVariant = {
  id: 'fan',
  fits: (c) => photos(c).length >= 2,
  duration: () => 3200,
  build(x) {
    const { b, c } = x;
    backdrop(x);
    const up = b.upright;
    const tb = b.pick({ upright: b.in(0, 0, 1, 0.22), other: b.in(0, 0.04, 0.42, 0.6) });
    title(x, 'showcase_title', c.subline || c.headline, tb, up ? 0.075 : 0.07);
    const strip = b.pick({ upright: b.in(0, 0.3, 1, 0.66), other: b.in(0.46, 0.02, 0.54, 0.96) });
    const list = photos(c).slice(0, 3);
    const n = list.length;
    const cw = strip.w * (n === 2 ? 0.5 : 0.42);
    const chh = Math.min(strip.h * 0.86, cw * 1.4);
    const order = n === 3 ? [0, 2, 1] : [0, 1];
    order.forEach((i) => {
      const xx = strip.x + (n === 1 ? (strip.w - cw) / 2 : i * ((strip.w - cw) / (n - 1)));
      const yy = strip.y + (strip.h - chh) / 2 + (i === 1 ? -chh * 0.05 : chh * 0.03);
      const turn = n === 3 ? [-6, 0, 6][i] : [-4, 4][i];
      photoCard(x, `look_${i + 1}`, list[i], { x: xx, y: yy, w: cw, h: chh }, turn, {
        keys: flyKeys(b, 400 + i * 220, { y: Math.round(b.H * 0.6) }, turn * 2),
        emphasis: emph('float', 1600 + i * 120, 3000, 0),
      });
    });
  },
};

const showcaseSingle: BlockVariant = {
  id: 'single',
  fits: (c) => photos(c).length >= 1,
  duration: () => 3000,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    const up = b.upright;
    const tb = b.pick({ upright: b.in(0, 0, 1, 0.16), other: b.in(0, 0.06, 0.4, 0.5) });
    title(x, 'showcase_title', label(c), tb, up ? 0.08 : 0.075);
    const ph: Box = b.pick({
      upright: b.in(0.06, 0.2, 0.88, 0.62),
      other: { x: F.x + F.w * 0.46, y: F.y, w: F.w * 0.54, h: F.h },
    });
    photoCard(x, 'product_photo', photos(c)[0], ph, 0, {
      keys: [
        { at: 0, scale: 0.85, opacity: 0, easing: 'out-expo' },
        { at: 700, scale: 1, opacity: 1, easing: 'linear' },
        { at: -1, scale: 1.04, opacity: 1 },
      ],
    });
    if (c.price) {
      const h = b.s(0.1);
      b.chip(
        sc,
        'price',
        c.price,
        up
          ? { x: F.x + F.w - b.s(0.02), y: ph.y + ph.h - h * 0.6, h, align: 'right' }
          : { x: F.x, y: F.y + F.h * 0.62, h },
        {
          bg: look(x).hi,
          color: look(x).onHi,
          font: x.p.heading,
          weight: 900,
          size: Math.round(h * 0.46),
        },
        {
          enter: step('pop', 1100, 500, 'spring-bouncy'),
          emphasis: emph('wiggle', 1700, 500, 2, 300),
        },
      );
    }
    if (!up && c.subline)
      b.text(
        sc,
        'subline',
        c.subline,
        b.in(0, 0.36, 0.4, 0.24),
        body(x, 0.034, { verticalAlign: 'top', maxLines: 4 }),
        { enter: step('fade', 800, 500) },
      );
  },
};

const showcasePhone: BlockVariant = {
  id: 'phone',
  fits: (c) => photos(c).length >= 1,
  duration: () => 3400,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    const up = b.upright;
    const ratio = 2.05;
    const ph = b.pick({ upright: F.h * 0.62, other: F.h * 0.92 });
    const pw = ph / ratio;
    const phone: Box = up
      ? { x: b.cx - pw / 2, y: F.y + F.h * 0.34, w: pw, h: ph }
      : { x: F.x + F.w * 0.72 - pw / 2, y: F.y + (F.h - ph) / 2, w: pw, h: ph };
    const tb = up
      ? b.in(0, 0, 1, 0.28)
      : { x: F.x, y: F.y + F.h * 0.08, w: phone.x - F.x - b.s(0.05), h: F.h * 0.5 };
    title(x, 'showcase_title', c.subline || c.headline, tb, up ? 0.072 : 0.07);
    const keys: RelKey[] = [
      { at: 0, y: Math.round(b.H * 0.7), easing: 'out-expo' },
      { at: 800, y: 0, easing: 'ease-in-out' },
      { at: -1, y: -b.s(0.015) },
    ];
    b.shape(
      sc,
      'phone_frame',
      phone,
      {
        fill: solid('#0A0A12'),
        borderRadius: pw * 0.16,
        shadow: { x: 0, y: b.s(0.03), blur: b.s(0.08), color: '#00000080' },
      },
      { keys },
    );
    const bz = pw * 0.045;
    b.image(
      sc,
      'phone_screen',
      photos(c)[0],
      { x: phone.x + bz, y: phone.y + bz, w: pw - bz * 2, h: ph - bz * 2 },
      { borderRadius: pw * 0.12 },
      { keys },
    );
    benefits(c)
      .slice(0, 2)
      .forEach((text, i) => {
        const h = b.s(0.075);
        const at = up
          ? {
              x: i ? F.x + F.w : F.x,
              y: phone.y + ph * (0.25 + i * 0.3),
              h,
              align: (i ? 'right' : 'left') as 'left' | 'right',
            }
          : { x: F.x, y: F.y + F.h * (0.66 + i * 0.14), h, align: 'left' as const };
        b.chip(
          sc,
          `phone_chip_${i + 1}`,
          text,
          { ...at, maxW: up ? F.w * 0.6 : phone.x - F.x - b.s(0.04) },
          {
            bg: i ? '#FFFFFF' : look(x).hi,
            color: i ? x.p.ink : look(x).onHi,
            font: x.p.heading,
            weight: 700,
          },
          {
            enter: step('pop', 1000 + i * 250, 450, 'spring-bouncy'),
            emphasis: emph('float', 1600 + i * 250, 2000, 0),
          },
        );
      });
  },
};

// ---------------------------------------------------------------------------------------------
// Benefits

const benefitsCards: BlockVariant = {
  id: 'cards',
  fits: (c) => benefits(c).length >= 2,
  duration: (c) => 2400 + benefits(c).slice(0, 4).length * 300,
  build(x) {
    const { b, sc, c, p } = x;
    backdrop(x);
    const up = b.upright;
    const list = benefits(c).slice(0, 4);
    const n = list.length;
    title(x, 'benefits_title', b.tx(GEN_TITLES.benefits), b.in(0, 0, 1, up ? 0.2 : 0.22));
    const area = b.pick({
      upright: b.in(0, 0.26, 1, 0.66),
      square: b.in(0, 0.3, 1, 0.5),
      wide: b.in(0, 0.3, 1, 0.56),
    });
    const cols = up ? (n === 3 ? 1 : 2) : n;
    const rows = Math.ceil(n / cols);
    const g = b.s(0.03);
    const cw = (area.w - g * (cols - 1)) / cols;
    const ch = Math.min((area.h - g * (rows - 1)) / rows, up && cols === 1 ? b.s(0.2) : cw * 1.2);
    const colors = [p.primary, ...p.extras];
    list.forEach((text, i) => {
      const bx = area.x + (i % cols) * (cw + g);
      const by = area.y + Math.floor(i / cols) * (ch + g);
      const m: MotionOpts = { enter: step('pop', 600 + i * 260, 500, 'spring-bouncy') };
      b.shape(
        sc,
        `benefit_card_${i + 1}`,
        { x: bx, y: by, w: cw, h: ch },
        {
          fill: solid(colors[i % colors.length]),
          borderRadius: Math.min(cw, ch) * 0.14,
          shadow: { x: 0, y: b.s(0.012), blur: b.s(0.04), color: '#0000003A' },
        },
        m,
      );
      const inline = up && cols === 1;
      const num = Math.min(ch * 0.5, cw * 0.35);
      b.text(
        sc,
        `benefit_number_${i + 1}`,
        String(i + 1),
        inline
          ? { x: bx + ch * 0.2, y: by, w: num, h: ch }
          : { x: bx + cw * 0.1, y: by + ch * 0.06, w: cw * 0.8, h: ch * 0.42 },
        {
          fontFamily: p.heading,
          fontWeight: 900,
          fontSize: Math.round(num),
          color: '#FFFFFF',
          align: 'left',
          maxLines: 1,
        },
        m,
      );
      b.text(
        sc,
        `benefit_${i + 1}`,
        text,
        inline
          ? { x: bx + ch * 0.2 + num + b.s(0.03), y: by, w: cw - num - ch * 0.4 - b.s(0.03), h: ch }
          : { x: bx + cw * 0.1, y: by + ch * 0.5, w: cw * 0.8, h: ch * 0.44 },
        {
          fontFamily: p.heading,
          fontWeight: 700,
          fontSize: b.s(up ? 0.045 : 0.034),
          color: '#FFFFFF',
          align: 'left',
          verticalAlign: inline ? 'middle' : 'bottom',
          maxLines: 3,
        },
        m,
      );
    });
  },
};

const benefitsChecklist: BlockVariant = {
  id: 'checklist',
  fits: (c) => benefits(c).length >= 2,
  duration: (c) => 2400 + benefits(c).slice(0, 5).length * 250,
  build(x) {
    const { b, c } = x;
    backdrop(x);
    const up = b.upright;
    const list = benefits(c).slice(0, 5);
    title(x, 'benefits_title', b.tx(GEN_TITLES.benefits), b.in(0, 0, 1, up ? 0.2 : 0.22));
    const area = b.in(0, up ? 0.28 : 0.3, b.wide ? 0.7 : 1, up ? 0.62 : 0.66);
    // Rows spread over the frame on upright videos, packed elsewhere
    const h = Math.min(b.s(up ? 0.13 : 0.09), (area.h / list.length) * 0.8);
    const gap = Math.min(
      up ? h * 0.7 : h * 0.3,
      (area.h - h * list.length) / Math.max(1, list.length - 1),
    );
    const dir = x.rnd() < 0.5 ? 'left' : 'right';
    list.forEach((text, i) =>
      row(
        x,
        `benefit_${i + 1}`,
        text,
        { x: area.x, y: area.y + i * (h + gap), w: area.w, h },
        { keys: slideKeys(b, 700 + i * 220, dir) },
      ),
    );
  },
};

const benefitsChips: BlockVariant = {
  id: 'chips',
  fits: (c) => benefits(c).length >= 2,
  duration: () => 3000,
  build(x) {
    const { b, sc, c, p } = x;
    backdrop(x);
    const F = b.F;
    const list = benefits(c).slice(0, 5);
    title(
      x,
      'benefits_title',
      c.subline || b.tx(GEN_TITLES.benefits),
      b.in(0, 0, 1, b.upright ? 0.24 : 0.26),
      0.08,
      'center',
    );
    const h = b.s(0.09);
    const top = F.y + F.h * (b.upright ? 0.34 : 0.36);
    const room = (F.y + F.h * 0.94 - top - h) / Math.max(1, list.length - 1);
    const step_ = b.upright ? room : Math.min(h * 1.45, room);
    const colors = [look(x).hi, '#FFFFFF', p.extras[0], p.extras[1], '#FFFFFF'];
    list.forEach((text, i) => {
      const align = (['left', 'right', 'center'] as const)[i % 3];
      const ax = align === 'left' ? F.x + F.w * 0.02 : align === 'right' ? F.x + F.w * 0.98 : b.cx;
      const bg = plain(colors[i % colors.length]);
      b.chip(
        sc,
        `benefit_${i + 1}`,
        text,
        { x: ax, y: top + i * step_, h, align, maxW: F.w * 0.9 },
        {
          bg,
          color: bg === '#FFFFFF' ? p.ink : '#FFFFFF',
          font: p.heading,
          weight: 800,
          extra: { shadow: { x: 0, y: b.s(0.01), blur: b.s(0.03), color: '#00000033' } },
        },
        {
          enter: step('pop', 600 + i * 260, 450, 'spring-bouncy'),
          emphasis: emph('float', 1200 + i * 200, 2400, 0, 0, { intensity: 2 }),
        },
      );
    });
  },
};

// ---------------------------------------------------------------------------------------------
// Stats

const statsCounters: BlockVariant = {
  id: 'counters',
  fits: (c) => stats(c).length >= 1,
  duration: () => 3000,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const up = b.upright;
    const list = stats(c).slice(0, 4);
    title(x, 'stats_title', b.tx(GEN_TITLES.stats), b.in(0, 0, 1, 0.18));
    const cols = list.length === 1 ? 1 : up ? (list.length === 3 ? 1 : 2) : list.length;
    const rows = Math.ceil(list.length / cols);
    const area = b.in(0, up ? 0.24 : 0.28, 1, up ? 0.7 : 0.66);
    const cw = area.w / cols;
    const ch = area.h / rows;
    list.forEach((s, i) => {
      const bx = area.x + (i % cols) * cw;
      const by = area.y + Math.floor(i / cols) * ch;
      b.text(
        sc,
        `stat_value_${i + 1}`,
        s.value,
        { x: bx, y: by, w: cw * 0.94, h: ch * 0.6 },
        head(x, Math.min(0.2, 0.42 / Math.max(cols, rows)), {
          color: look(x).hi,
          fontWeight: 900,
          verticalAlign: 'bottom',
          maxLines: 1,
        }),
        { enter: step('pop', 500 + i * 300, 500, 'spring-bouncy') },
      );
      if (s.label)
        b.text(
          sc,
          `stat_label_${i + 1}`,
          s.label,
          { x: bx, y: by + ch * 0.62, w: cw * 0.94, h: ch * 0.32 },
          body(x, 0.036, { verticalAlign: 'top', maxLines: 2 }),
          { enter: step('fade', 750 + i * 300, 400) },
        );
    });
  },
};

const statsBars: BlockVariant = {
  id: 'bars',
  clip: true,
  fits: (c) => stats(c).length >= 2,
  duration: () => 3200,
  build(x) {
    const { b, sc, c, p } = x;
    backdrop(x);
    const F = b.F;
    const list = stats(c).slice(0, 4);
    title(x, 'stats_title', b.tx(GEN_TITLES.stats), b.in(0, 0, 1, 0.2));
    const ry = F.y + F.h * 0.3;
    const rh = Math.min((F.y + F.h * 0.97 - ry) / list.length, b.s(b.upright ? 0.3 : 0.2));
    const colors = [look(x).hi, ...p.extras];
    list.forEach((s, i) => {
      const y = ry + i * rh;
      const delay = 600 + i * 320;
      b.text(
        sc,
        `stat_label_${i + 1}`,
        s.label || ' ',
        { x: F.x, y, w: F.w * 0.7, h: rh * 0.4 },
        body(x, 0.038, { fontWeight: 700, color: look(x).fg, maxLines: 1 }),
        { enter: step('fade', delay - 200, 400) },
      );
      const track: Box = { x: F.x, y: y + rh * 0.46, w: F.w, h: rh * 0.2 };
      b.shape(sc, `stat_track_${i + 1}`, track, {
        fill: solid(x.theme === 'dark' ? '#FFFFFF24' : '#0000000F'),
        borderRadius: track.h,
      });
      b.shape(
        sc,
        `stat_bar_${i + 1}`,
        { ...track, w: track.w * (1 - i * 0.14) },
        { fill: solid(colors[i % colors.length]), borderRadius: track.h },
        { enter: step('wipe', delay, 700, 'out-quart') },
      );
      b.text(
        sc,
        `stat_value_${i + 1}`,
        s.value,
        { x: F.x + F.w * 0.7, y, w: F.w * 0.3, h: rh * 0.4 },
        head(x, 0.05, { color: plain(colors[i % colors.length]), align: 'right', maxLines: 1 }),
        { enter: step('pop', delay + 600, 450, 'spring-bouncy') },
      );
    });
  },
};

// ---------------------------------------------------------------------------------------------
// Gallery

const galleryGrid: BlockVariant = {
  id: 'grid',
  fits: (c) => photos(c).length >= 3,
  duration: (c) => 2200 + Math.min(6, photos(c).length) * 200,
  build(x) {
    const { b, c } = x;
    backdrop(x);
    const list = photos(c).slice(0, 6);
    const n = list.length >= 6 ? 6 : list.length >= 4 ? 4 : 3;
    title(
      x,
      'gallery_title',
      c.brand || b.tx(GEN_TITLES.gallery),
      b.in(0, 0, 1, b.upright ? 0.14 : 0.18),
    );
    const cols = b.wide ? n : n === 4 ? 2 : 3;
    const rows = Math.ceil(n / cols);
    const grid = b.pick({
      upright: b.in(0, 0.2, 1, 0.76),
      square: b.in(0.06, 0.24, 0.88, 0.74),
      wide: b.in(0, 0.3, 1, 0.66),
    });
    const g = b.s(0.02);
    const tw = Math.min((grid.w - g * (cols - 1)) / cols, ((grid.h - g * (rows - 1)) / rows) * 0.8);
    const th = tw / 0.8;
    const gx = grid.x + (grid.w - (tw * cols + g * (cols - 1))) / 2;
    const gy = grid.y + (grid.h - (th * rows + g * (rows - 1))) / 2;
    list.slice(0, n).forEach((src, i) => {
      b.image(
        x.sc,
        `grid_${i + 1}`,
        src,
        { x: gx + (i % cols) * (tw + g), y: gy + Math.floor(i / cols) * (th + g), w: tw, h: th },
        { borderRadius: Math.round(tw * 0.07) },
        { enter: step('pop', 500 + i * 180, 500, 'spring-bouncy') },
      );
    });
  },
};

const galleryPan: BlockVariant = {
  id: 'pan',
  fits: (c) => photos(c).length >= 3,
  duration: () => 3400,
  build(x) {
    const { b, c } = x;
    backdrop(x);
    const F = b.F;
    const list = photos(c).slice(0, 6);
    title(
      x,
      'gallery_title',
      c.subline || c.brand || b.tx(GEN_TITLES.gallery),
      b.in(0, 0, 1, b.upright ? 0.2 : 0.24),
    );
    const h = b.pick({ upright: F.h * 0.5, square: F.h * 0.6, wide: F.h * 0.62 });
    const w = h * 0.75;
    const g = b.s(0.03);
    const y = F.y + F.h * (b.upright ? 0.32 : 0.32);
    const total = list.length * (w + g) - g;
    // The strip runs right to left through the scene
    const travel = Math.max(0, total - F.w) + b.s(0.2);
    const keys: RelKey[] = [
      { at: 0, x: Math.round(b.s(0.2)), easing: 'linear' },
      { at: -1, x: -Math.round(travel) },
    ];
    list.forEach((src, i) =>
      b.image(
        x.sc,
        `strip_${i + 1}`,
        src,
        { x: F.x + i * (w + g), y, w, h },
        {
          borderRadius: Math.round(w * 0.07),
          shadow: { x: 0, y: b.s(0.015), blur: b.s(0.04), color: '#00000055' },
        },
        { keys, enter: step('fade', i * 120, 400) },
      ),
    );
  },
};

// ---------------------------------------------------------------------------------------------
// Offer

const offerHit: BlockVariant = {
  id: 'hit',
  themes: ['dark'],
  fits: (c) => Boolean(c.offer),
  duration: () => 2800,
  build(x) {
    const { b, sc, c, p } = x;
    backdrop(x);
    const F = b.F;
    const up = b.upright;
    b.text(
      sc,
      'offer_note',
      c.offerNote || b.tx(GEN_TITLES.offerNote),
      b.in(0, up ? 0.06 : 0.04, 1, 0.08),
      body(x, 0.04, {
        fontWeight: 800,
        align: 'center',
        letterSpacing: b.s(0.004),
        textTransform: 'uppercase',
        maxLines: 1,
      }),
      { enter: step('fade', 100, 400) },
    );
    b.text(
      sc,
      'offer',
      c.offer!,
      b.in(0, up ? 0.16 : 0.13, 1, up ? 0.3 : 0.38),
      head(x, 0.3, { color: look(x).hi, fontWeight: 900, align: 'center', maxLines: 1 }),
      {
        enter: step('pop', 400, 600, 'spring-bouncy'),
        emphasis: emph('wiggle', 1100, 500, 2, 300),
      },
    );
    benefits(c)
      .slice(0, 2)
      .forEach((text, i) => {
        const h = b.s(0.08);
        b.chip(
          sc,
          `offer_chip_${i + 1}`,
          text,
          { x: b.cx, y: F.y + F.h * (up ? 0.52 : 0.56) + i * h * 1.3, h, align: 'center' },
          { bg: '#FFFFFF', color: p.ink, font: p.heading, weight: 800 },
          { keys: slideKeys(b, 1000 + i * 250, i ? 'right' : 'left') },
        );
      });
    b.addFx(sc, 'equalizer', 300, 2400, {
      count: 32,
      height: up ? 12 : 16,
      size: 84,
      y: up ? 80 : 88,
      color: plain(look(x).hi),
      accent: plain(p.extras[0]),
    });
  },
};

const offerPrice: BlockVariant = {
  id: 'price',
  fits: (c) => Boolean(c.price),
  duration: () => 2800,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    const up = b.upright;
    title(x, 'offer_title', label(c), b.in(0, up ? 0.04 : 0.02, 1, 0.2), 0.08, 'center');
    const old = c.oldPrice;
    if (old) {
      const ob = b.in(0.2, up ? 0.3 : 0.28, 0.6, 0.12);
      b.text(
        sc,
        'old_price',
        old,
        ob,
        head(x, 0.08, { color: look(x).muted, align: 'center', fontWeight: 700, maxLines: 1 }),
        { enter: step('fade', 300, 400) },
      );
      b.shape(
        sc,
        'old_price_strike',
        b.centered(b.cx, ob.y + ob.h / 2, F.w * 0.3, b.s(0.012)),
        { fill: solid('#EF4444'), borderRadius: b.s(0.01) },
        { enter: step('wipe', 800, 300, 'out-quart') },
      );
    }
    const pb = b.in(0, up ? 0.44 : 0.42, 1, up ? 0.24 : 0.3);
    const burst = Math.min(pb.h * 1.4, F.w * 0.8);
    b.shape(
      sc,
      'price_burst',
      b.centered(b.cx, pb.y + pb.h / 2, burst, burst),
      { shape: 'ellipse', fill: solid(plain(look(x).hi)), opacity: 0.18 },
      { enter: step('zoom-in', 1000, 500, 'out-expo'), emphasis: emph('breathe', 1500, 2000, 0) },
    );
    b.text(
      sc,
      'price',
      c.price!,
      pb,
      head(x, 0.22, { color: look(x).hi, fontWeight: 900, align: 'center', maxLines: 1 }),
      { enter: step('pop', 1100, 600, 'spring-bouncy'), emphasis: emph('pulse', 1900, 800, 0) },
    );
    if (c.offer) {
      const h = b.s(0.09);
      b.chip(
        sc,
        'offer',
        c.offer,
        { x: b.cx, y: pb.y + pb.h + b.s(0.04), h, align: 'center' },
        { bg: look(x).hi, color: look(x).onHi, font: x.p.heading, weight: 900 },
        { enter: step('pop', 1500, 450, 'spring-bouncy') },
      );
    }
  },
};

// ---------------------------------------------------------------------------------------------
// Quote

const quoteBig: BlockVariant = {
  id: 'big',
  fits: (c) => Boolean(c.quote),
  duration: () => 3400,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    const s = b.s(0.06);
    const total = s * 5 + s * 0.3 * 4;
    for (let i = 0; i < 5; i++)
      b.icon(
        sc,
        `star_${i + 1}`,
        'star',
        { x: b.cx - total / 2 + i * s * 1.3, y: F.y + F.h * 0.1, w: s, h: s },
        plain(look(x).hi) === '#FFFFFF' ? '#FACC15' : plain(look(x).hi),
        { enter: step('pop', 200 + i * 120, 400, 'spring-bouncy') },
      );
    b.text(
      sc,
      'quote',
      `“${c.quote!.replace(/^["“«]|["”»]$/g, '')}”`,
      b.in(0, 0.24, 1, 0.5),
      head(x, b.upright ? 0.095 : 0.07, { align: 'center', fontWeight: 700, lineHeight: 1.2 }),
      { enter: step('words-rise', 800, 450), split: { by: 'word', stagger: 45 } },
    );
    if (c.author)
      b.text(
        sc,
        'author',
        `— ${c.author}`,
        b.in(0, 0.78, 1, 0.1),
        body(x, 0.04, { align: 'center', maxLines: 1 }),
        { enter: step('fade', 1900, 500) },
      );
  },
};

// ---------------------------------------------------------------------------------------------
// Call to action

const ctaTitle = (x: BlockCtx) => x.c.brand || x.c.headline;

const ctaButton: BlockVariant = {
  id: 'button',
  fits: () => true,
  duration: () => 3000,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    const up = b.upright;
    const cw = up ? F.w : F.w * 0.6;
    b.text(
      sc,
      'cta_title',
      ctaTitle(x),
      { x: F.x, y: F.y + F.h * 0.1, w: cw, h: F.h * 0.3 },
      head(x, 0.12, { verticalAlign: 'bottom' }),
      { enter: step('words-rise', 150, 600), split: { by: 'word', stagger: 100 } },
    );
    if (c.subline || c.site)
      b.text(
        sc,
        'cta_subline',
        c.site || c.subline!,
        { x: F.x, y: F.y + F.h * 0.42, w: cw, h: F.h * 0.12 },
        body(x, 0.04, { verticalAlign: 'top', maxLines: 2 }),
        { enter: step('fade', 700, 500) },
      );
    button(
      x,
      c.cta || b.tx(GEN_TITLES.cta),
      { x: F.x, y: F.y + F.h * (up ? 0.6 : 0.62), h: b.s(0.1) },
      1100,
    );
    const ic = b.s(up ? 0.24 : 0.28);
    const box: Box = up
      ? { x: F.x + F.w - ic, y: F.y + F.h * 0.76, w: ic, h: ic }
      : { x: F.x + F.w - ic - F.w * 0.06, y: F.y + (F.h - ic) / 2, w: ic, h: ic };
    const m: MotionOpts = {
      enter: step('pop', 1300, 600, 'spring-bouncy'),
      emphasis: emph('float', 1900, 3000, 0),
    };
    if (c.logo) b.image(sc, 'cta_logo', c.logo, box, { fit: 'contain' }, m);
    else {
      b.shape(
        sc,
        'cta_icon',
        box,
        {
          fill: lin(135, plain(look(x).hi), plain(x.p.extras[2] ?? look(x).hi)),
          borderRadius: ic * 0.26,
          shadow: { x: 0, y: b.s(0.02), blur: b.s(0.06), color: '#00000055' },
        },
        m,
      );
      b.icon(
        sc,
        'cta_icon_glyph',
        pick(x, ['sparkles', 'rocket', 'heart', 'star', 'zap'] as const),
        b.centered(box.x + ic / 2, box.y + ic / 2, ic * 0.5, ic * 0.5),
        plain(look(x).onHi),
        m,
      );
    }
  },
};

const ctaChecklist: BlockVariant = {
  id: 'checklist',
  fits: (c) => benefits(c).length >= 2,
  duration: () => 3200,
  build(x) {
    const { b, c } = x;
    backdrop(x);
    const F = b.F;
    const up = b.upright;
    const cw = up ? F.w : F.w * 0.62;
    b.text(
      x.sc,
      'cta_title',
      ctaTitle(x),
      { x: F.x, y: F.y, w: cw, h: F.h * 0.22 },
      head(x, 0.11, { verticalAlign: 'top' }),
      { enter: step('words-rise', 150, 600), split: { by: 'word', stagger: 100 } },
    );
    const list = benefits(c).slice(0, 3);
    const h = b.s(up ? 0.1 : 0.085);
    list.forEach((text, i) =>
      row(
        x,
        `check_${i + 1}`,
        text,
        { x: F.x, y: F.y + F.h * 0.3 + i * h * 1.25, w: up ? F.w * 0.92 : cw, h },
        { keys: slideKeys(b, 800 + i * 230, 'left') },
      ),
    );
    button(x, c.cta || b.tx(GEN_TITLES.cta), { x: F.x, y: F.y + F.h * 0.84, h: b.s(0.1) }, 1700);
    if (c.logo) {
      const s = b.s(up ? 0.18 : 0.22);
      const box: Box = up
        ? { x: F.x + F.w - s, y: F.y + F.h * 0.84 - s * 0.3, w: s, h: s }
        : { x: F.x + F.w - s - F.w * 0.06, y: F.y + (F.h - s) / 2, w: s, h: s };
      b.image(
        x.sc,
        'cta_logo',
        c.logo,
        box,
        { fit: 'contain' },
        { enter: step('pop', 1300, 600, 'spring-bouncy'), emphasis: emph('float', 1900, 3000, 0) },
      );
    }
  },
};

const ctaLogo: BlockVariant = {
  id: 'logo',
  fits: (c) => Boolean(c.logo),
  duration: () => 2800,
  build(x) {
    const { b, sc, c } = x;
    backdrop(x);
    const F = b.F;
    const s = b.s(0.26);
    b.image(
      sc,
      'cta_logo',
      c.logo!,
      b.centered(b.cx, F.y + F.h * 0.26, s, s),
      { fit: 'contain' },
      { enter: step('zoom-in', 100, 700, 'out-expo'), emphasis: emph('breathe', 900, 2400, 0) },
    );
    if (c.brand)
      b.text(
        sc,
        'cta_title',
        c.brand,
        b.in(0, 0.44, 1, 0.14),
        head(x, 0.1, { align: 'center', maxLines: 1 }),
        { enter: step('rise', 500, 500) },
      );
    button(
      x,
      c.cta || b.tx(GEN_TITLES.cta),
      { x: b.cx, y: F.y + F.h * 0.64, h: b.s(0.1), align: 'center' },
      900,
    );
    if (c.site)
      b.text(
        sc,
        'cta_site',
        c.site,
        b.in(0, 0.8, 1, 0.08),
        body(x, 0.038, { align: 'center', maxLines: 1 }),
        { enter: step('fade', 1300, 500) },
      );
  },
};

export const BLOCKS: BlockDef[] = [
  {
    id: 'hook',
    place: 'first',
    name: t('Hook', 'Гачок', 'Зацепка', 'Haczyk', 'Cârlig', 'Gancho'),
    variants: [hookAccent, hookCenter, hookPhoto, hookKinetic],
  },
  {
    id: 'showcase',
    place: 'middle',
    name: t('Product', 'Товар', 'Товар', 'Produkt', 'Produs', 'Producto'),
    variants: [showcaseFan, showcaseSingle, showcasePhone],
  },
  {
    id: 'benefits',
    place: 'middle',
    name: t('Benefits', 'Переваги', 'Преимущества', 'Zalety', 'Beneficii', 'Ventajas'),
    variants: [benefitsCards, benefitsChecklist, benefitsChips],
  },
  {
    id: 'stats',
    place: 'middle',
    name: t('Numbers', 'Цифри', 'Цифры', 'Liczby', 'Cifre', 'Cifras'),
    variants: [statsCounters, statsBars],
  },
  {
    id: 'gallery',
    place: 'middle',
    name: t('Gallery', 'Галерея', 'Галерея', 'Galeria', 'Galerie', 'Galería'),
    variants: [galleryGrid, galleryPan],
  },
  {
    id: 'offer',
    place: 'middle',
    name: t('Offer', 'Пропозиція', 'Предложение', 'Oferta', 'Ofertă', 'Oferta'),
    variants: [offerHit, offerPrice],
  },
  {
    id: 'quote',
    place: 'middle',
    name: t('Review', 'Відгук', 'Отзыв', 'Opinia', 'Recenzie', 'Reseña'),
    variants: [quoteBig],
  },
  {
    id: 'cta',
    place: 'last',
    name: t('Call to action', 'Заклик', 'Призыв', 'Wezwanie', 'Îndemn', 'Llamada'),
    variants: [ctaButton, ctaChecklist, ctaLogo],
  },
];
export const BLOCK_MAP = new Map(BLOCKS.map((b) => [b.id, b]));

export function findVariant(block: BlockId, variant: string): BlockVariant | undefined {
  return BLOCK_MAP.get(block)?.variants.find((v) => v.id === variant);
}
